
import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

interface Message {
  type: 'user' | 'ai';
  content: string;
}

interface TypeWriterProps {
  text: string;
  speed?: number;
  onComplete?: () => void;
}

const TypeWriter = ({ text, speed = 30, onComplete }: TypeWriterProps) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prevIndex => prevIndex + 1);
      }, speed);
      
      return () => clearTimeout(timeout);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, speed, onComplete]);
  
  return <>{displayText}</>;
};

interface AIChatProps {
  chatMessages: Message[];
  setChatMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  showTypingEffect: boolean;
}

const AIChat = ({ chatMessages, setChatMessages, showTypingEffect }: AIChatProps) => {
  const [chatInput, setChatInput] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    setChatMessages(prev => [...prev, {type: 'user', content: chatInput}]);
    const userMessage = chatInput;
    setChatInput('');
    
    setTimeout(() => {
      let aiResponse = "I've analyzed your request and can help enhance your prompts. For better results, consider adding specific technical requirements and use cases to your description.";
      
      if (userMessage.toLowerCase().includes("react")) {
        aiResponse = "For React applications, I recommend specifying state management approach (Redux, Context API), component architecture (atomic design?), and styling method (CSS-in-JS, Tailwind?). Would you like me to update your prompts with these React-specific details?";
      } else if (userMessage.toLowerCase().includes("mobile")) {
        aiResponse = "For mobile-responsive applications, focus on specifying breakpoints, touch interactions, and progressive enhancement. Would you like me to optimize your prompts for mobile-first development?";
      }
      
      setChatMessages(prev => [...prev, {type: 'ai', content: aiResponse}]);
    }, 1500);
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChatMessage();
    }
  };

  return (
    <Card className="bg-gray-800/30 backdrop-blur border-white/5">
      <CardContent className="p-4">
        <h3 className="text-lg font-medium mb-3 flex items-center">
          <Bot className="mr-2 h-5 w-5 text-purple-400" />
          <span>AI Assistant</span>
        </h3>
        
        <div 
          ref={chatContainerRef}
          className="max-h-[300px] overflow-y-auto pr-2 mb-4"
        >
          <AnimatePresence>
            {chatMessages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className={`p-3 rounded-lg mb-3 ${
                  message.type === 'user' 
                    ? 'ml-auto max-w-[80%] bg-indigo-600/20 border border-indigo-500/20' 
                    : 'max-w-[100%] bg-gray-700/20 border border-gray-600/20'
                }`}
              >
                {index === chatMessages.length - 1 && message.type === 'ai' && showTypingEffect ? (
                  <TypeWriter text={message.content} speed={10} />
                ) : (
                  <p className="text-sm leading-relaxed">
                    {message.content}
                  </p>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        
        <div className="flex gap-2">
          <Textarea 
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleChatKeyDown}
            placeholder="Ask for clarification or enhancements..."
            className="bg-gray-900/50 border-white/10 focus:border-primary text-sm resize-none"
            rows={1}
          />
          <Button 
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 flex-shrink-0"
            onClick={handleSendChatMessage}
            disabled={!chatInput.trim()}
          >
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIChat;
